import { useEffect, useRef, useCallback } from 'react';

// Minimum pixel count where worker overhead is worth it
const MIN_PIXELS_FOR_WORKER = 80000; // ~283x283 or larger

// Number of workers in the pool (use available CPU cores, max 8)
const WORKER_POOL_SIZE = Math.min(navigator.hardwareConcurrency || 4, 8);

/**
 * Check if Web Workers are supported in this browser
 */
function supportsWebWorkers() {
  return typeof Worker !== 'undefined';
}

/**
 * Workers cannot load from file:// (origin null) in most browsers.
 * Skip worker creation so we fall back to main-thread rendering without errors.
 */
function canUseWorkers() {
  if (!supportsWebWorkers()) return false;
  try {
    const isFile = typeof window !== 'undefined' &&
      (window.location.protocol === 'file:' || window.location.origin === 'null');
    return !isFile;
  } catch {
    return false;
  }
}

/**
 * Hook to manage a pool of Web Workers for parallel fractal rendering
 * Uses multiple CPU cores to render different chunks simultaneously
 * 
 * Falls back to main thread when:
 * - Browser doesn't support Web Workers
 * - Canvas is too small (worker overhead not worth it)
 * - Workers fail to initialize
 */
export function useFractalWorker() {
  const workersRef = useRef([]);
  const requestIdRef = useRef(0);
  const pendingRequestsRef = useRef(new Map());
  const workerFailedRef = useRef(false);
  const nextWorkerRef = useRef(0);

  // Initialize worker pool on mount
  useEffect(() => {
    const pendingRequests = pendingRequestsRef.current;
    if (!canUseWorkers()) {
      if (typeof window !== 'undefined' && window.location?.protocol === 'file:') {
        console.info('Workers disabled for file:// — use a local server (e.g. npx serve build) for worker support');
      } else if (!supportsWebWorkers()) {
        console.info('Web Workers not supported, using main thread');
      }
      return;
    }

    try {
      // Create pool of workers
      for (let i = 0; i < WORKER_POOL_SIZE; i++) {
        const worker = new Worker(
          new URL('./fractal.worker.js', import.meta.url)
        );

        worker.onmessage = (e) => {
          const { id, type, data, error } = e.data;
          const pending = pendingRequestsRef.current.get(id);
          
          if (pending) {
            pendingRequestsRef.current.delete(id);
            if (type === 'error') {
              pending.reject(new Error(error));
            } else {
              pending.resolve(data);
            }
          }
        };

        worker.onerror = (e) => {
          console.error('Worker error:', e);
        };

        workersRef.current.push(worker);
      }
      
      console.info(`Worker pool initialized with ${WORKER_POOL_SIZE} workers`);
    } catch (err) {
      console.warn('Worker pool initialization failed:', err.message);
      workersRef.current = [];
      workerFailedRef.current = true;
    }

    return () => {
      workersRef.current.forEach(worker => worker.terminate());
      workersRef.current = [];
      pendingRequests.forEach((p) => p.reject(new Error('Workers terminated')));
      pendingRequests.clear();
    };
  }, []);

  /**
   * Get next worker from pool (round-robin)
   */
  const getNextWorker = useCallback(() => {
    if (workersRef.current.length === 0) return null;
    const worker = workersRef.current[nextWorkerRef.current];
    nextWorkerRef.current = (nextWorkerRef.current + 1) % workersRef.current.length;
    return worker;
  }, []);

  /**
   * Send a message to a specific worker
   */
  const postMessageToWorker = useCallback((worker, type, params) => {
    return new Promise((resolve, reject) => {
      if (!worker) {
        reject(new Error('Worker not available'));
        return;
      }
      const id = ++requestIdRef.current;
      pendingRequestsRef.current.set(id, { resolve, reject });
      worker.postMessage({ type, id, params });
    });
  }, []);

  /**
   * Render full canvas using parallel workers
   * Splits canvas into horizontal chunks and renders in parallel
   */
  const renderFull = useCallback(async (params) => {
    const { canvasWidth, canvasHeight } = params;
    const numWorkers = workersRef.current.length;
    
    if (numWorkers === 0) {
      throw new Error('No workers available');
    }

    // Split into horizontal strips for each worker
    const rowsPerWorker = Math.ceil(canvasHeight / numWorkers);
    const chunks = [];

    for (let i = 0; i < numWorkers; i++) {
      const startY = i * rowsPerWorker;
      const endY = Math.min(startY + rowsPerWorker, canvasHeight);
      if (startY >= canvasHeight) break;

      chunks.push({
        worker: workersRef.current[i],
        startY,
        height: endY - startY
      });
    }

    // Render all chunks in parallel
    const results = await Promise.all(
      chunks.map(({ worker, startY, height }) =>
        postMessageToWorker(worker, 'renderFull', {
          ...params,
          // Render only this chunk
          startY,
          regionHeight: height
        })
      )
    );

    // Combine results into single pixel array (chunks already Uint8ClampedArray from transfer)
    const pixels = new Uint8ClampedArray(canvasWidth * canvasHeight * 4);
    let offset = 0;
    for (const chunk of results) {
      pixels.set(chunk, offset);
      offset += chunk.length;
    }
    return pixels;
  }, [postMessageToWorker]);

  /**
   * Request an incremental pan render (uses single worker for simplicity)
   */
  const renderPan = useCallback(async (params) => {
    const worker = getNextWorker();
    return postMessageToWorker(worker, 'renderPan', params);
  }, [getNextWorker, postMessageToWorker]);

  const isWorkerAvailable = useCallback(() => {
    return workersRef.current.length > 0 && !workerFailedRef.current;
  }, []);

  const shouldUseWorker = useCallback((canvasWidth, canvasHeight) => {
    if (!isWorkerAvailable()) return false;
    const pixelCount = canvasWidth * canvasHeight;
    return pixelCount >= MIN_PIXELS_FOR_WORKER;
  }, [isWorkerAvailable]);

  return {
    renderFull,
    renderPan,
    isWorkerAvailable,
    shouldUseWorker
  };
}

/**
 * Parse a colorFunc to extract serializable parameters for the worker
 * Since functions can't be sent to workers, we need to serialize them
 */
export function serializeColorFunc(colorFunc) {
  // Get the function source to analyze it
  const funcStr = colorFunc.toString();
  
  // Default values
  const params = {
    r: 255,
    gScale: 7,
    b: 0,
    aScale: 15,
    aFixed: null,
    useMu: true
  };

  // Check if it uses numIterations instead of mu
  if (funcStr.includes('numIterations')) {
    params.useMu = false;
  }

  // Extract multipliers using regex
  // Pattern: mu * N or numIterations * N
  const gScaleMatch = funcStr.match(/(?:mu|numIterations)\s*\*\s*(\d+)/);
  if (gScaleMatch) {
    params.gScale = parseInt(gScaleMatch[1], 10);
  }

  // Look for alpha scale - usually the second multiplier or a fixed value
  // Pattern: [ 255, ... , 0, mu * N ] or [ 255, ..., 0, 255 ]
  const alphaMatches = funcStr.match(/,\s*(?:(\d+)|(?:mu|numIterations)\s*\*\s*(\d+))\s*\]/);
  if (alphaMatches) {
    if (alphaMatches[1]) {
      // Fixed alpha value
      params.aFixed = parseInt(alphaMatches[1], 10);
    } else if (alphaMatches[2]) {
      // Alpha scale
      params.aScale = parseInt(alphaMatches[2], 10);
    }
  }

  return params;
}
